import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTeam } from '../contexts/TeamContext';
import { useAuth } from '../contexts/AuthContext';
import { TeamLogo } from './common/TeamLogo';
import { Dropdown } from './common/Dropdown';
import { apiFetch } from '../utils/httpClient';

const SelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  background: var(--color-surface);
  border-radius: 12px;
  border: 1px solid var(--color-border);
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 100vh;
  box-shadow: var(--shadow-soft);
  backdrop-filter: blur(24px);
  
  &:focus {
    outline: none;
    box-shadow: var(--shadow-soft);
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-soft);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1rem;
    border-radius: 8px;
    max-height: 85vh;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    gap: 0.75rem;
    max-height: 80vh;
  }
`;

const SelectorTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  text-align: center;
`;

const CurrentMatchup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--color-surface-alt);
  border-radius: 8px;
  border: 1px solid var(--color-border);
`;

const TeamPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TeamName = styled.span`
  font-size: 0.8rem;
  color: var(--color-text-secondary);
`;

const VSText = styled.span`
  font-size: 1rem;
  font-weight: bold;
  color: var(--color-accent);
`;

const ConfigSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background: var(--color-surface-alt);
  border-radius: 8px;
  border: 1px solid var(--color-border);
`;

const SectionTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
`;

const TeamRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const TeamLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--color-text-primary);
  min-width: 100px;

  @media (max-width: 480px) {
    min-width: auto;
    width: 100%;
  }
`;

const TeamSelect = styled.select`
  flex: 1;
  background: linear-gradient(140deg, var(--color-surface), var(--color-surface-alt));
  border: 1px solid var(--color-border);
  border-radius: 1.5rem;
  color: var(--color-text-primary);
  padding: 0.75rem 3rem 0.75rem 1.25rem;
  font-size: 0.9rem;
  font-family: var(--font-family-sans);
  font-weight: 500;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.3s var(--transition-smooth);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  appearance: none;
  background-image: 
    linear-gradient(140deg, var(--color-surface), var(--color-surface-alt)),
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ff4343' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat, no-repeat;
  background-position: center, right 1rem center;
  background-size: 100%, 12px;

  &:hover {
    border-color: rgba(255, 77, 77, 0.5);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px rgba(255, 43, 43, 0.2), 0 6px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  option {
    background: var(--color-surface);
    color: var(--color-text-primary);
    padding: 0.75rem 1.25rem;
  }

  &::-ms-expand {
    display: none;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const ConfigGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const ConfigLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--color-text-primary);
  min-width: 80px;

  @media (max-width: 480px) {
    min-width: auto;
    width: 100%;
  }
`;

const ConfigSelect = styled.select`
  background: linear-gradient(140deg, var(--color-surface), var(--color-surface-alt));
  border: 1px solid var(--color-border);
  border-radius: 1.5rem;
  color: var(--color-text-primary);
  padding: 0.75rem 3rem 0.75rem 1.25rem;
  font-size: 0.9rem;
  font-family: var(--font-family-sans);
  font-weight: 500;
  min-width: 120px;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.3s var(--transition-smooth);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  appearance: none;
  background-image: linear-gradient(140deg, var(--color-surface), var(--color-surface-alt));
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100%;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid var(--color-accent);
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    border-color: rgba(255, 77, 77, 0.5);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px rgba(255, 43, 43, 0.2), 0 6px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  option {
    background: var(--color-surface);
    color: var(--color-text-primary);
    padding: 0.75rem 1.25rem;
  }

  &::-ms-expand {
    display: none;
  }

  @media (max-width: 480px) {
    min-width: auto;
    width: 100%;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s var(--transition-smooth);
  box-sizing: border-box;
  min-height: 44px; /* iOS minimum touch target */

  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, var(--color-accent), var(--color-accent-light));
    color: white;
    box-shadow: 0 4px 12px rgba(var(--color-accent-rgb), 0.3);

    &:hover:not(:disabled), &:active:not(:disabled) {
      background: linear-gradient(135deg, var(--color-accent-light), var(--color-accent));
      box-shadow: 0 6px 16px rgba(var(--color-accent-rgb), 0.5);
      transform: translateY(-1px);
    }
  ` : `
    background: var(--color-surface-alt);
    color: var(--color-text-primary);
    border: 1px solid var(--color-border);

    &:hover:not(:disabled), &:active:not(:disabled) {
      background: var(--color-surface);
      border-color: var(--color-border-strong);
      color: var(--color-text-primary);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
  }
`;

const ErrorMessage = styled.div`
  color: var(--color-error);
  font-size: 0.8rem;
  text-align: center;
  padding: 0.5rem;
  background: rgba(var(--color-error-rgb), 0.1);
  border-radius: 4px;
  border: 1px solid rgba(var(--color-error-rgb), 0.2);
`;

const LoadingMessage = styled.div`
  color: var(--color-text-secondary);
  font-size: 0.8rem;
  text-align: center;
  padding: 0.5rem;
`;

interface TeamSelectorProps {
  onClose?: () => void;
}

export const TeamSelector: React.FC<TeamSelectorProps> = ({ onClose }) => {
  const {
    allTeams,
    leagues,
    selectedLeague,
    setSelectedLeague,
    stages,
    isStagesLoading,
    stagesError,
    matchupSummary,
    isConfiguring,
    error: contextError,
    setTeamConfiguration,
    loadLeagues,
    loadTeamsByLeague,
    loadStages
  } = useTeam();

  const { user } = useAuth();
  const isSystemAdmin = user?.role === 'system_admin' || user?.role === 'admin';  // Support legacy 'admin'
  const hasOrganization = !!user?.organization_id;

  const [selectedUserTeam, setSelectedUserTeam] = useState('');
  const [selectedOpponentTeam, setSelectedOpponentTeam] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');
  const [selectedMatchupType, setSelectedMatchupType] = useState('regular');
  const [localError, setLocalError] = useState<string | null>(null);
  const [availableSeasons, setAvailableSeasons] = useState<string[]>([]);
  const [userOrganizationTeam, setUserOrganizationTeam] = useState<any | null>(null);
  const [userOrganization, setUserOrganization] = useState<any | null>(null);
  const [loadingOrgTeam, setLoadingOrgTeam] = useState(false);
  const [availableOpponents, setAvailableOpponents] = useState<any[]>([]);
  const [availableStages, setAvailableStages] = useState<any[]>([]);
  const [loadingOpponents, setLoadingOpponents] = useState(false);
  const [loadingStages, setLoadingStages] = useState(false);

  // Fetch leagues on mount
  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        await loadLeagues();
      } catch (err) {
        console.error('Failed to fetch leagues:', err);
        setLocalError('Failed to load leagues');
      }
    };

    fetchLeagues();
  }, [loadLeagues]);

  // Fetch opponents (teams) when league is selected
  useEffect(() => {
    const fetchOpponents = async () => {
      if (!selectedLeague?.id) {
        setAvailableOpponents([]);
        return;
      }

      setLoadingOpponents(true);
      try {
        // Use context's loadTeamsByLeague which populates allTeams
        await loadTeamsByLeague(selectedLeague.id);
        
        // Use the allTeams data from context instead of making another API call
        setAvailableOpponents(allTeams);
      } catch (err) {
        console.error('Failed to fetch opponents:', err);
        setLocalError('Failed to load opponents');
      } finally {
        setLoadingOpponents(false);
      }
    };

    fetchOpponents();
  }, [selectedLeague, loadTeamsByLeague, allTeams]);

  // Fetch stages when league is selected
  useEffect(() => {
    const fetchStages = async () => {
      if (!selectedLeague?.id) {
        setAvailableStages([]);
        return;
      }

      setLoadingStages(true);
      try {
        // Use context's loadStages
        await loadStages(selectedLeague.id);
        
        // Also fetch directly from API to ensure we have the data
        const response = await apiFetch(`/api/v1/leagues/${encodeURIComponent(selectedLeague.id)}/stages`);
        if (response.ok) {
          const stagesData = await response.json();
          setAvailableStages(stagesData);
        }
      } catch (err) {
        console.error('Failed to fetch stages:', err);
        setLocalError('Failed to load stages');
      } finally {
        setLoadingStages(false);
      }
    };

    fetchStages();
  }, [selectedLeague, loadStages]);

  // Fetch user's organization and team if they belong to an organization
  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (!hasOrganization || isSystemAdmin) return;

      setLoadingOrgTeam(true);
      try {
        // Fetch organization data (includes leagues field)
        const orgResponse = await apiFetch('/api/v1/organizations/me');
        if (orgResponse.ok) {
          const org = await orgResponse.json();
          setUserOrganization(org);
        }

        // Fetch organization team
        const teamResponse = await apiFetch('/api/v1/organizations/me/team');
        if (teamResponse.ok) {
          const team = await teamResponse.json();
          setUserOrganizationTeam(team);
          // Auto-set as selected user team
          setSelectedUserTeam(team.teamId);
        }
      } catch (err) {
        console.error('Failed to fetch organization data:', err);
        setLocalError('Failed to load organization data');
      } finally {
        setLoadingOrgTeam(false);
      }
    };

    fetchOrganizationData();
  }, [hasOrganization, isSystemAdmin]);

  // Fetch available seasons from backend when league is selected
  useEffect(() => {
    const fetchSeasons = async () => {
      if (!selectedLeague?.id) return;

      try {
        const response = await apiFetch(`/api/v1/leagues/${encodeURIComponent(selectedLeague.id)}/seasons`);
        if (response.ok) {
          const seasons = await response.json();
    setAvailableSeasons(seasons);
    
          // Auto-select first season if none selected
          if (seasons.length > 0 && !selectedSeason) {
      setSelectedSeason(seasons[0]);
    }
        }
      } catch (err) {
        console.error('Failed to fetch seasons:', err);
        // Fallback to client-side generation
        const currentYear = new Date().getFullYear();
        const fallbackSeasons: string[] = [];
        for (let i = 0; i < 5; i++) {
          const year = currentYear - i;
          fallbackSeasons.push(`${year}-${(year + 1).toString().slice(-2)}`);
        }
        setAvailableSeasons(fallbackSeasons);
        if (fallbackSeasons.length > 0 && !selectedSeason) {
          setSelectedSeason(fallbackSeasons[0]);
        }
      }
    };

    fetchSeasons();
  }, [selectedLeague]);

  // Load saved configuration when component mounts
  useEffect(() => {
    if (allTeams.length > 0) {
      // First, try to load from saved configuration
      if (matchupSummary) {
        setSelectedUserTeam(matchupSummary.user_team.teamId);
        setSelectedOpponentTeam(matchupSummary.opponent_team.teamId);
        setSelectedSeason(matchupSummary.season || '');
        setSelectedMatchupType(matchupSummary.matchup_type || 'regular');
      } else {
        // No saved configuration - start with empty selections
        console.log('🔄 TeamSelector: No saved configuration, starting with empty selections');
        setSelectedUserTeam('');
        setSelectedOpponentTeam('');
      }
    }
  }, [allTeams, matchupSummary]);

  // Set default stage when stages are loaded
  useEffect(() => {
    if (stages.length > 0 && !selectedMatchupType) {
      // Set the first stage as default
      setSelectedMatchupType(stages[0].stageId);
      console.log('🔄 TeamSelector: Set default stage to:', stages[0].stageId);
    }
  }, [stages, selectedMatchupType]);

  const handleSetConfiguration = async () => {
    if (!selectedLeague) {
      setLocalError('Please select a league');
      return;
    }

    if (!selectedUserTeam || !selectedOpponentTeam) {
      setLocalError('Please select both teams');
      return;
    }

    if (selectedUserTeam === selectedOpponentTeam) {
      setLocalError('User team and opponent team cannot be the same');
      return;
    }

    if (!selectedSeason) {
      setLocalError('Please select a season');
      return;
    }

    setLocalError(null);
    
    try {
      // Ensure teams are loaded before attempting configuration
      if (allTeams.length === 0) {
        setLocalError('Teams are still loading. Please wait a moment and try again.');
        return;
      }

      const success = await setTeamConfiguration({
        user_team_abbreviation: selectedUserTeam,
        opponent_team_abbreviation: selectedOpponentTeam,
        season: selectedSeason,
        matchup_type: selectedMatchupType,
        league: selectedLeague.name
      });

      if (success) {
        onClose?.();
      } else {
        // Get error from context if available, otherwise use generic message
        const errorMessage = contextError || 'Failed to set team configuration. Please try again.';
        setLocalError(errorMessage);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while setting team configuration.';
      setLocalError(errorMessage);
      console.error('Error setting team configuration:', err);
    }
  };


  const getUserTeamInfo = (teamId: string) => {
    return allTeams.find(team => team.teamId === teamId);
  };

  const getOpponentTeamInfo = (teamId: string) => {
    return allTeams.find(team => team.teamId === teamId);
  };

  return (
    <SelectorContainer tabIndex={-1} onFocus={(e) => e.currentTarget.blur()}>
      <SelectorTitle>Configure Team Matchup</SelectorTitle>
      
      {/* Dynamic logo bar that updates based on current selections */}
      <CurrentMatchup>
        <TeamPreview>
          <TeamLogo 
            teamName={getUserTeamInfo(selectedUserTeam || matchupSummary?.user_team.teamId || '')?.teamName || ''} 
            size={24} 
          />
          <TeamName>{getUserTeamInfo(selectedUserTeam || matchupSummary?.user_team.teamId || '')?.teamName || 'Your team'}</TeamName>
        </TeamPreview>
        <VSText>VS</VSText>
        <TeamPreview>
          <TeamLogo 
            teamName={getOpponentTeamInfo(selectedOpponentTeam || matchupSummary?.opponent_team.teamId || '')?.teamName || ''} 
            size={24} 
          />
          <TeamName>{getOpponentTeamInfo(selectedOpponentTeam || matchupSummary?.opponent_team.teamId || '')?.teamName || 'Opponent team'}</TeamName>
        </TeamPreview>
      </CurrentMatchup>

      <ConfigSection>
        <SectionTitle>League Selection</SectionTitle>
        
        <ConfigGroup>
          <ConfigLabel>League:</ConfigLabel>
          <Dropdown
            options={(() => {
              // For organization users, filter leagues to show only organization's team league
              if (hasOrganization && !isSystemAdmin && userOrganization?.leagues && userOrganization.leagues.length > 0) {
                const orgLeagues = userOrganization.leagues;
                return leagues
                  .filter(league => orgLeagues.some((orgLeague: string) => 
                    league.id.toLowerCase() === orgLeague.toLowerCase() || 
                    league.abbreviation.toLowerCase() === orgLeague.toLowerCase() ||
                    league.name.toLowerCase() === orgLeague.toLowerCase()
                  ))
                  .map(league => ({
                    value: league.id,
                    label: league.name === league.name.toUpperCase() 
                      ? league.name 
                      : league.name.charAt(0).toUpperCase() + league.name.slice(1).toLowerCase()
                  }));
              }
              // For system admins, show all leagues
              return leagues.map(league => ({
                value: league.id,
                label: league.name === league.name.toUpperCase() 
                  ? league.name 
                  : league.name.charAt(0).toUpperCase() + league.name.slice(1).toLowerCase()
              }));
            })()}
            value={selectedLeague?.id || ''}
            placeholder="Select a league..."
            onChange={async (value) => {
              const availableLeagues = (() => {
                if (hasOrganization && !isSystemAdmin && userOrganization?.leagues && userOrganization.leagues.length > 0) {
                  const orgLeagues = userOrganization.leagues;
                  return leagues.filter(league => orgLeagues.some((orgLeague: string) => 
                    league.id.toLowerCase() === orgLeague.toLowerCase() || 
                    league.abbreviation.toLowerCase() === orgLeague.toLowerCase() ||
                    league.name.toLowerCase() === orgLeague.toLowerCase()
                  ));
                }
                return leagues;
              })();
              const league = availableLeagues.find(l => l.id === value);
              
              // Clear all selections when league changes
              setSelectedUserTeam('');
              setSelectedOpponentTeam('');
              setSelectedSeason('');
              setSelectedMatchupType('regular');
              
              // Set the new league and load teams for it
              setSelectedLeague(league || null);
              
              // Load teams for the new league
              if (league) {
                await loadTeamsByLeague(league.id);
              }
            }}
            disabled={isConfiguring}
          />
        </ConfigGroup>
      </ConfigSection>

      <ConfigSection>
        <SectionTitle>Team Selection</SectionTitle>
        
        <TeamRow>
          <TeamLabel>Your Team:</TeamLabel>
          {isSystemAdmin ? (
            // System admin: can customize
            <Dropdown
              options={allTeams.map(team => ({
                value: team.teamId,
                label: team.teamName
              }))}
              value={selectedUserTeam}
              placeholder={!selectedLeague ? 'Please select a league first...' : 'Select your team...'}
              onChange={(value) => setSelectedUserTeam(value)}
              disabled={isConfiguring || !selectedLeague}
            />
          ) : hasOrganization && userOrganizationTeam ? (
            // Organization user: fixed team, read-only
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1rem',
              background: 'var(--color-surface-alt)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-primary)',
              fontSize: 'var(--font-size-sm)'
            }}>
              <TeamLogo teamName={userOrganizationTeam.teamName} size={20} />
              <span>{userOrganizationTeam.teamName}</span>
            </div>
          ) : loadingOrgTeam ? (
            <div style={{
              padding: '0.75rem 1rem',
              color: 'var(--color-text-secondary)',
              fontStyle: 'italic'
            }}>
              Loading your team...
            </div>
          ) : (
            // Fallback for users without organization
          <Dropdown
            options={allTeams.map(team => ({
              value: team.teamId,
              label: team.teamName
            }))}
            value={selectedUserTeam}
            placeholder={!selectedLeague ? 'Please select a league first...' : 'Select your team...'}
            onChange={(value) => setSelectedUserTeam(value)}
            disabled={isConfiguring || !selectedLeague}
          />
          )}
        </TeamRow>

        <TeamRow>
          <TeamLabel>Opponent:</TeamLabel>
          <Dropdown
            options={allTeams.map(team => ({
              value: team.teamId,
              label: team.teamName
            }))}
            value={selectedOpponentTeam}
            placeholder={!selectedLeague ? 'Please select a league first...' : 'Select opponent...'}
            onChange={(value) => setSelectedOpponentTeam(value)}
            disabled={isConfiguring || !selectedLeague}
          />
        </TeamRow>
      </ConfigSection>

      <ConfigSection>
        <SectionTitle>Season Configuration</SectionTitle>
        
        <ConfigGroup>
          <ConfigLabel>Season:</ConfigLabel>
          <Dropdown
            options={availableSeasons.map(season => ({
              value: season,
              label: season
            }))}
            value={selectedSeason}
            placeholder="Select season..."
            onChange={(value) => setSelectedSeason(value)}
            disabled={isConfiguring}
          />
        </ConfigGroup>
        
        <ConfigGroup>
          <ConfigLabel>Stage:</ConfigLabel>
          <Dropdown
            options={
              (isStagesLoading || loadingStages)
                ? [{ value: '', label: 'Loading stages...' }]
                : !selectedLeague
                ? [{ value: '', label: 'Please select a league first...' }]
                : (stages.length === 0 && availableStages.length === 0)
                ? [{ value: '', label: 'No stages available' }]
                : (stages.length > 0 ? stages : availableStages).map((stage: any) => ({
                    value: stage.stageId || stage.id || stage,
                    label: stage.stageName || stage.name || stage
                  }))
            }
            value={selectedMatchupType}
            placeholder="Select stage..."
            onChange={(value) => setSelectedMatchupType(value)}
            disabled={isConfiguring || (isStagesLoading && loadingStages) || !selectedLeague}
          />
        </ConfigGroup>
      </ConfigSection>

      {(contextError || localError) && (
        <ErrorMessage>
          {localError || contextError}
        </ErrorMessage>
      )}

      {isConfiguring && (
        <LoadingMessage>
          Updating team configuration...
        </LoadingMessage>
      )}

      <ButtonRow>
        <ActionButton
          $variant="primary"
          onClick={handleSetConfiguration}
          disabled={isConfiguring || !selectedLeague || !selectedUserTeam || !selectedOpponentTeam || !selectedSeason}
          data-track-event="set_configuration_click"
          data-track-label="Set Configuration"
          data-track-context="team_selector"
        >
          {isConfiguring ? 'Setting...' : 'Set Configuration'}
        </ActionButton>
        {onClose && (
          <ActionButton
            $variant="secondary"
            onClick={onClose}
            disabled={isConfiguring}
            data-track-event="close_configuration_modal"
            data-track-label="Cancel Configuration"
            data-track-context="team_selector"
          >
            Cancel
          </ActionButton>
        )}
      </ButtonRow>
    </SelectorContainer>
  );
};